import { losseContentParse, LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import { LosseLink, type LoaderData } from '@ubo/losse-sjedel'
import type {
  Component_Footer,
  Component_Footer_MenuForEmployers,
  Component_Footer_MenuForPros,
  Component_Footer_MenuGeneral
} from '~/graphql/types'
import Logo from '../elements/Logo'
import useGeneralgeneralInfo from '~/hooks/useGeneralInfo'
import ToggleVacancyAlert from '../elements/ToggleVacancyAlert'
import useGlobalCounters from '~/hooks/useGlobalCounters'
import Stars from '../elements/Stars'
import { useState } from 'react'
import useIsMobile from '~/hooks/useIsMobile'
import { motion } from 'framer-motion'
import clsx from 'clsx'
import Button from '../elements/Button'

export interface FooterLoaderData extends Omit<LoaderData, 'footer'> {
  footer: Component_Footer
}

export default function Footer() {
  const { footer } = useLoaderData<FooterLoaderData>()
  const generalInfo = useGeneralgeneralInfo()

  return (
    <footer data-component="Footer" className="bg-site-base section section--with-bg text-white">
      <div className="container pt-8 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 px-2 lg:px-6">
          <div className="lg:col-span-5 -mt-4">
            {/* 0,210650887573964 */}
            <Logo className="h-[42px] w-[200px] max-md:hidden" />
            <Reviews className="md:hidden" />

            <div className="mt-10 flex max-sm:flex-col max-sm:gap-4 font-light">
              <div className="sm:w-1/2">
                <LosseLink
                  to={generalInfo?.address?.url}
                  target={generalInfo?.address?.target}
                  className="text-white hover:underline flex flex-col gap-2"
                >
                  <span className="block">{generalInfo?.address?.title?.split('|')[0]}</span>
                  <span className="block">{generalInfo?.address?.title?.split('|')[1]}</span>
                </LosseLink>
              </div>
              <div className="sm:w-1/2 flex flex-col gap-2 font-light">
                <LosseLink src={generalInfo?.phone} className="hover:underline font-bold" />
                <LosseLink src={generalInfo?.email} className="hover:underline text-site-accent font-bold" />
              </div>
            </div>

            <div className="flex max-sm:flex-col sm:items-center mt-10 gap-6 lg:gap-12">
              <Button
                to={generalInfo?.profieldPortal?.url}
                target={generalInfo?.profieldPortal?.target}
                className="md:px-6 max-sm:w-full text-lg btn--conversion"
              >
                {generalInfo?.profieldPortal?.title}
              </Button>
              {generalInfo?.badge && <LossePlaatjie className="w-[100px]" maxwidth={500} src={generalInfo?.badge} />}
            </div>

            <div className="mt-8">
              <ToggleVacancyAlert className="[&_>_span]:font-light" />
            </div>

            <div className="mt-10 flex items-center gap-6">
              <Reviews className="max-md:hidden" />
              <div className="flex items-center gap-6">
                <LosseLink
                  to={generalInfo?.linkedin?.url}
                  target={generalInfo?.linkedin?.target}
                  className="text-white hover:text-site-conversion-hover"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none" className="h-6">
                    <path d="M5.14343 7.1875H0V23H5.14343V7.1875Z" fill="currentColor" />
                    <path
                      d="M19.1525 7.3728C19.098 7.35596 19.0458 7.33687 18.9885 7.32114C18.9194 7.30542 18.8504 7.29251 18.7802 7.28071C18.5073 7.22625 18.208 7.1875 17.8571 7.1875C14.8586 7.1875 12.9568 9.36846 12.3301 10.2107V7.1875H7.18726V23H12.3307V14.375C12.3307 14.375 16.2175 8.96135 17.8576 12.9375V23H22.9999V12.3294C22.9999 9.94009 21.3626 7.94949 19.153 7.3728H19.1525Z"
                      fill="currentColor"
                    />
                    <path
                      d="M5.03113 2.51562C5.03113 3.90483 3.90474 5.03125 2.51556 5.03125C1.12639 5.03125 0 3.90483 0 2.51562C0 1.12642 1.12639 0 2.51556 0C3.90474 0 5.03113 1.12642 5.03113 2.51562Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="sr-only">{generalInfo?.linkedin?.title}</span>
                </LosseLink>
                <LosseLink
                  to={generalInfo?.instagram?.url}
                  target={generalInfo?.instagram?.target}
                  className="text-white hover:text-site-conversion-hover"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6">
                    <g>
                      <path
                        d="M12 2.16094C15.2063 2.16094 15.5859 2.175 16.8469 2.23125C18.0188 2.28281 18.6516 2.47969 19.0734 2.64375C19.6313 2.85938 20.0344 3.12188 20.4516 3.53906C20.8734 3.96094 21.1313 4.35938 21.3469 4.91719C21.5109 5.33906 21.7078 5.97656 21.7594 7.14375C21.8156 8.40937 21.8297 8.78906 21.8297 11.9906C21.8297 15.1969 21.8156 15.5766 21.7594 16.8375C21.7078 18.0094 21.5109 18.6422 21.3469 19.0641C21.1313 19.6219 20.8687 20.025 20.4516 20.4422C20.0297 20.8641 19.6313 21.1219 19.0734 21.3375C18.6516 21.5016 18.0141 21.6984 16.8469 21.75C15.5813 21.8062 15.2016 21.8203 12 21.8203C8.79375 21.8203 8.41406 21.8062 7.15313 21.75C5.98125 21.6984 5.34844 21.5016 4.92656 21.3375C4.36875 21.1219 3.96563 20.8594 3.54844 20.4422C3.12656 20.0203 2.86875 19.6219 2.65313 19.0641C2.48906 18.6422 2.29219 18.0047 2.24063 16.8375C2.18438 15.5719 2.17031 15.1922 2.17031 11.9906C2.17031 8.78438 2.18438 8.40469 2.24063 7.14375C2.29219 5.97187 2.48906 5.33906 2.65313 4.91719C2.86875 4.35938 3.13125 3.95625 3.54844 3.53906C3.97031 3.11719 4.36875 2.85938 4.92656 2.64375C5.34844 2.47969 5.98594 2.28281 7.15313 2.23125C8.41406 2.175 8.79375 2.16094 12 2.16094ZM12 0C8.74219 0 8.33438 0.0140625 7.05469 0.0703125C5.77969 0.126563 4.90313 0.332812 4.14375 0.628125C3.35156 0.9375 2.68125 1.34531 2.01563 2.01562C1.34531 2.68125 0.9375 3.35156 0.628125 4.13906C0.332812 4.90313 0.126563 5.775 0.0703125 7.05C0.0140625 8.33437 0 8.74219 0 12C0 15.2578 0.0140625 15.6656 0.0703125 16.9453C0.126563 18.2203 0.332812 19.0969 0.628125 19.8563C0.9375 20.6484 1.34531 21.3188 2.01563 21.9844C2.68125 22.65 3.35156 23.0625 4.13906 23.3672C4.90313 23.6625 5.775 23.8687 7.05 23.925C8.32969 23.9812 8.7375 23.9953 11.9953 23.9953C15.2531 23.9953 15.6609 23.9812 16.9406 23.925C18.2156 23.8687 19.0922 23.6625 19.8516 23.3672C20.6391 23.0625 21.3094 22.65 21.975 21.9844C22.6406 21.3188 23.0531 20.6484 23.3578 19.8609C23.6531 19.0969 23.8594 18.225 23.9156 16.95C23.9719 15.6703 23.9859 15.2625 23.9859 12.0047C23.9859 8.74688 23.9719 8.33906 23.9156 7.05938C23.8594 5.78438 23.6531 4.90781 23.3578 4.14844C23.0625 3.35156 22.6547 2.68125 21.9844 2.01562C21.3188 1.35 20.6484 0.9375 19.8609 0.632812C19.0969 0.3375 18.225 0.13125 16.95 0.075C15.6656 0.0140625 15.2578 0 12 0Z"
                        fill="currentColor"
                      />
                      <path
                        d="M12 5.83594C8.59688 5.83594 5.83594 8.59688 5.83594 12C5.83594 15.4031 8.59688 18.1641 12 18.1641C15.4031 18.1641 18.1641 15.4031 18.1641 12C18.1641 8.59688 15.4031 5.83594 12 5.83594ZM12 15.9984C9.79219 15.9984 8.00156 14.2078 8.00156 12C8.00156 9.79219 9.79219 8.00156 12 8.00156C14.2078 8.00156 15.9984 9.79219 15.9984 12C15.9984 14.2078 14.2078 15.9984 12 15.9984Z"
                        fill="currentColor"
                      />
                      <path
                        d="M19.8469 5.59239C19.8469 6.38926 19.2 7.03145 18.4078 7.03145C17.6109 7.03145 16.9688 6.38457 16.9688 5.59239C16.9688 4.79551 17.6156 4.15332 18.4078 4.15332C19.2 4.15332 19.8469 4.8002 19.8469 5.59239Z"
                        fill="currentColor"
                      />
                    </g>
                  </svg>
                  <span className="sr-only">{generalInfo?.instagram?.title}</span>
                </LosseLink>
                <LosseLink
                  to={generalInfo?.facebook?.url}
                  target={generalInfo?.facebook?.target}
                  className="text-white hover:text-site-conversion-hover text-[0px]"
                >
                  <svg width="13" height="24" viewBox="0 0 13 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6">
                    <g>
                      <path
                        d="M8.08988 24V13.0533H11.7628L12.3138 8.78588H8.08988V6.06176C8.08988 4.82664 8.43146 3.98492 10.2046 3.98492L12.4625 3.98399V0.167076C12.072 0.116334 10.7317 0 9.1717 0C5.91418 0 3.68402 1.98836 3.68402 5.63912V8.78588H0V13.0533H3.68402V24H8.08988Z"
                        fill="currentColor"
                      />
                    </g>
                  </svg>
                  <span className="sr-only">{generalInfo?.facebook?.title}</span>
                </LosseLink>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Menu title="Voor pro's" items={footer?.menuForPros} />
              </div>
              <div>
                <Menu title="Voor werkgevers" items={footer?.menuForEmployers} />
              </div>
              <div>
                <Menu title="Algemeen" items={footer?.menuGeneral} />
              </div>
            </div>
          </div>
          <div className="mt-2 flex--center">
            {/* 0,210650887573964 */}
            <Logo className="h-[42px] w-[200px] md:hidden" />
          </div>
        </div>
      </div>
      <div className="flex justify-center text-sm pt-10 lg:-mb-16 pb-4">
        <a href="https://www.ubo.agency/" target="_blank" rel="noreferrer" className="text-white/40 hover:text-white hover:underline">
          Gerealiseerd door UBO Agency
        </a>
      </div>
    </footer>
  )
}

export function Reviews({ className }: { className?: string }) {
  const globalCounters = useGlobalCounters()

  return (
    <LosseLink
      to="/beoordelingen/"
      className={clsx('bg-white text-site-base group hover:bg-site-grey-light flex items-center gap-2 px-3 py-1', className)}
    >
      <div className="flex items-start font-bold">
        <span className="text-2xl">{globalCounters?.reviews?.average?.toString()?.split('.')[0]}.</span>
        <span className="text-base mt-[1px]">{globalCounters?.reviews?.average?.toString()?.split('.')[1]}</span>
      </div>
      <Stars rating={globalCounters?.reviews?.average} size="small" />
      <span className="text-sm md:text-xs font-light md:font-bold group-hover:underline">
        uit {globalCounters?.reviews?.total} beoordelingen
      </span>
    </LosseLink>
  )
}

function Menu({
  title,
  items
}: {
  title: string
  items: Component_Footer_MenuGeneral[] | Component_Footer_MenuForPros[] | Component_Footer_MenuForEmployers[]
}) {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(!isMobile)

  return (
    <div>
      <div
        role="button"
        className="flex justify-between items-center md:mb-6"
        onClick={() => {
          if (!isMobile) return

          setIsOpen(!isOpen)
        }}
      >
        <h3 className="text-lg font-bold">{title}</h3>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={clsx(isOpen && 'rotate-180', 'md:hidden transition-all')}
        >
          <g clipPath="url(#clip0_893_7479)">
            <path
              d="M0.749913 3.00004C0.848455 3.00006 0.946028 3.0195 1.03705 3.05725C1.12808 3.095 1.21078 3.15031 1.28041 3.22004L5.11591 7.05553C5.23199 7.17164 5.3698 7.26374 5.52148 7.32658C5.67316 7.38942 5.83573 7.42177 5.99991 7.42177C6.16409 7.42177 6.32666 7.38942 6.47834 7.32658C6.63002 7.26374 6.76783 7.17164 6.88391 7.05553L10.7149 3.22454C10.8564 3.08792 11.0458 3.01232 11.2425 3.01403C11.4391 3.01574 11.6272 3.09462 11.7663 3.23367C11.9053 3.37273 11.9842 3.56084 11.9859 3.75749C11.9876 3.95413 11.912 4.14359 11.7754 4.28504L7.94441 8.11603C7.42837 8.63112 6.72903 8.92041 5.99991 8.92041C5.27079 8.92041 4.57146 8.63112 4.05541 8.11603L0.219412 4.28004C0.114625 4.17512 0.0432905 4.04149 0.0144228 3.89605C-0.0144459 3.7506 0.000447678 3.59987 0.0572218 3.46288C0.113995 3.3259 0.2101 3.20882 0.333393 3.12644C0.456685 3.04406 0.60163 3.00007 0.749913 3.00004Z"
              fill="white"
            />
          </g>
        </svg>
      </div>
      <motion.nav
        animate={isOpen ? 'open' : 'closed'}
        variants={{
          open: {
            height: 'auto',
            opacity: 1
          },
          closed: {
            height: 0,
            opacity: 0
          }
        }}
        className="max-lg:overflow-hidden"
      >
        <ul className="flex flex-col gap-1 font-light max-md:pb-6 max-md:pt-4">
          {items?.map((edge, index) => (
            <li className="py-1" key={index}>
              <LosseLink
                className="rounded-full px-2 -mx-2 py-[2px] btn--pseudo--conversion"
                to={edge?.link?.url}
                target={edge?.link?.target}
              >
                {losseContentParse(edge?.link?.title)}
              </LosseLink>
            </li>
          ))}
          {title === 'Algemeen' && (
            <li className="py-1">
              <button
                type="button"
                className="rounded-full px-2 -mx-2 py-[2px] btn--pseudo--conversion"
                onClick={() => {
                  // @ts-ignore
                  if (typeof Cookiebot !== 'undefined') Cookiebot.show()
                }}
              >
                Cookies
              </button>
            </li>
          )}
        </ul>
      </motion.nav>
    </div>
  )
}
